import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { notify } from '@/lib/notify';
import { translate, useT } from '@/lib/i18n';
import type { ConsoleLocale } from '@/lib/api';
import { useOptionalSession } from './session';

/**
 * Console language, one click, beside the light/dark toggle — the same shape as
 * `ThemeSwitch` because it answers the same kind of question: which of two
 * presentations am I reading this in.
 *
 * **It shows the language you would switch TO, named in that language.**
 * `العربية` while the console is English, `English` while it is Arabic. A globe
 * icon would be the obvious choice and is the wrong one: it names the *category*
 * and not the destination, so a reader who cannot read the current UI learns
 * nothing from it — which is exactly the reader reaching for this control.
 *
 * Two locales is what makes a toggle honest (`ConsoleLocale` is a closed enum of
 * `en-AE` and `ar-AE`). **A third makes this a menu, not a cycle** — a cycle
 * through three is how someone ends up passing through a language they cannot
 * read to reach the one they can. `language-switch.test.tsx` counts the
 * catalogue's `languageName.*` keys and fails when a third appears, so that
 * decision is forced rather than discovered.
 *
 * This is the *operator's* display language and it persists to their account,
 * exactly as the Profile picker does — §28c's rule stands untouched: an
 * assessment is administered in the language its instance was pinned to, and
 * nothing here can reach that.
 */
export function LanguageSwitch() {
  const session = useOptionalSession();
  const t = useT();
  const [saving, setSaving] = useState(false);

  // Rendered by the topbar, which the login screen also uses. Without a session
  // there is no account to save a preference to.
  if (!session) return null;

  const { locale, setLocale } = session;
  const next: ConsoleLocale = locale === 'ar-AE' ? 'en-AE' : 'ar-AE';

  async function switchTo(target: ConsoleLocale) {
    setSaving(true);
    try {
      await setLocale(target);
      // `translate(key, target)`, never `t(key)` — `t` is closed over the
      // language being LEFT, so the confirmation for switching to Arabic would
      // arrive in English. Profile.tsx carries the long version of this note.
      notify.success(translate('profile.language.saved', target));
    } catch {
      notify.error(t('profile.language.failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={saving}
      // The accessible name says what pressing it does; the visible glyph is the
      // destination language's own name, which is legible in either direction.
      aria-label={t(next === 'ar-AE' ? 'chrome.languageToArabic' : 'chrome.languageToEnglish')}
      onClick={() => void switchTo(next)}
    >
      {/* `translate(key, next)` — the destination named in the DESTINATION's
          language. `t()` would print "Arabic" to an English console, which is
          the label a reader who wants Arabic is least able to use. */}
      <span aria-hidden="true" className="text-[13px] font-medium">
        {translate(`languageName.${next}`, next)}
      </span>
    </Button>
  );
}
